import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SafeImage from "../../../components/SafeImage";

/**
 * SRC-styled full-screen hero. Copy + accent + image come from the template
 * config so each store type shows its own headline / CTA / photo. Falls
 * back to a graceful monogram via SafeImage when no `image_url` yet.
 *
 * `basePath` is passed through so links honor the mount point (published
 * store `/site/<slug>/*` vs showroom `/template/<key>/*`).
 */
export default function Hero({ config, basePath = "" }) {
  const { brand, hero, copy } = config;
  const accent = brand?.accentColor || "#E60000";
  const eyebrow = hero?.eyebrow || "FW26 — Drop 03";
  const headline1 = hero?.headline1 || "STREETWEAR,";
  const headline2 = hero?.headline2 || "made";
  const headline3 = hero?.headline3 || "HEAVY.";
  const subhead = hero?.subhead || (copy?.home?.heroSubtitle) ||
    "Small batches. Made to last.";
  const ctaPrimary = hero?.ctaPrimary || copy?.home?.heroCta || "Shop The Drop";
  const ctaSecondary = hero?.ctaSecondary || "Explore Collection";
  const image = hero?.image || "";

  return (
    <section
      data-testid="hero-section"
      className="relative h-screen w-screen overflow-hidden grain"
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      <SafeImage
        src={image}
        alt={`${brand?.name || "Store"} hero`}
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: "cover" }}
        testId="hero-image"
        fallbackLabel={brand?.name || "Store"}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40 pointer-events-none" />
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      <div className="relative h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col justify-end pb-20 sm:pb-28">
        <div className="max-w-3xl text-white">
          <div className="inline-flex items-center gap-2 mb-6 reveal">
            <span className="w-8 h-px bg-white" />
            <span className="text-[11px] tracking-[0.3em] uppercase" data-testid="hero-eyebrow">{eyebrow}</span>
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tighter reveal reveal-delay-1"
          >
            {headline1}<br />
            <span className="italic font-heading font-normal" data-testid="hero-headline-2">{headline2}</span> <span data-testid="hero-headline-3">{headline3}</span>
          </h1>
          <p className="mt-6 max-w-md text-sm sm:text-base text-neutral-200 leading-relaxed reveal reveal-delay-2" data-testid="hero-subhead">
            {subhead}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 reveal reveal-delay-3">
            <Link
              to={`${basePath}${hero?.ctaPrimaryTo || "/shop"}`}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase transition-colors duration-300"
              style={{ ["--h" ]: accent }}
              onMouseEnter={(e) => { e.currentTarget.style.background = accent; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#000"; }}
            >
              {ctaPrimary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </Link>
            <Link
              to={`${basePath}${hero?.ctaSecondaryTo || "/shop"}`}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border border-white/70 text-white px-8 py-4 text-sm font-medium tracking-[0.18em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
            >
              {ctaSecondary}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
